import React, { useState } from 'react';
import axios from 'axios';
import {
  Button, IconButton, Select, MenuItem, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Typography, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useEditMode } from './EditModeContext';
import {
  EMPTY_STRUCTURED_STATE,
  STRUCTURED_TYPES,
  StructuredAssessmentFields,
  buildStructuredInputs,
  hasAnyStructuredInput,
} from './webPresenceAssessmentShared';

const ADDRESS_TYPES = [
  'INPUT_DATA',
  'SOS_REGISTERED_ADDRESS',
  'REGISTERED_AGENT_ADDRESS',
  'OFFICE_ADDRESS',
  'OTHER_ADDRESS',
];

const WEB_PRESENCE_TYPES = [
  'INSTAGRAM',
  'LINKEDIN',
  'TWITTER',
  'YOUTUBE',
  'FACEBOOK',
  'YELP',
  'GOOGLE_MAPS',
  'MAPQUEST',
  'YELLOWPAGES',
  'WEB_REFERENCE',
  'DOMAIN',
];

const EMAIL_TYPES = [
  'INPUT_DATA',
  'OWNS',
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AddAddressButton = ({ addresses, onUpdate, graphData, onUpdateGraph, entityId }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [associationType, setAssociationType] = useState('INPUT_DATA');

  if (!isEditMode) return null;

  const handleAdd = () => {
    if (!value.trim()) return;
    const entry = {
      id: crypto.randomUUID(),
      type: 'ADDRESS',
      association_type: [associationType],
      source: [{ source_url: 'Manual edit', fetched_at: new Date().toISOString() }],
      value: value.trim(),
      confidence: 100.0,
      assessments: [],
      data: null,
    };
    addChangeDescription(`Added address: ${value.trim()}`);
    onUpdate([...addresses, entry]);

    // Also add to graph if handlers provided
    if (graphData && onUpdateGraph && entityId) {
      const addrNodeId = `addr_${entry.id}`;
      const addrNode = {
        id: addrNodeId,
        label: value.trim(),
        type: 'ADDRESS',
        data: { confidence: 100, type: associationType === 'OFFICE_ADDRESS' ? 'OFFICE_ADDRESS' : 'OTHER_ADDRESS' },
      };
      const addrEdge = {
        label: associationType === 'OFFICE_ADDRESS' ? 'Office' : 'Other',
        type: 'has_address',
        source: entityId,
        target: addrNodeId,
        confidence: 0.0,
      };
      onUpdateGraph({
        ...graphData,
        nodes: [...(graphData.nodes || []), addrNode],
        edges: [...(graphData.edges || []), addrEdge],
      });
    }

    setOpen(false);
    setValue('');
    setAssociationType('INPUT_DATA');
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ borderColor: '#4566EB', color: '#4566EB' }}
      >
        Add address
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add address</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Address"
            placeholder="Full address"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={associationType}
              label="Type"
              onChange={(e) => setAssociationType(e.target.value)}
            >
              {ADDRESS_TYPES.map(t => (
                <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!value.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RemoveAddressButton = ({ addressId, addresses, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const handleRemove = (e) => {
    e.stopPropagation();
    const removed = addresses.find(a => a.id === addressId);
    const updated = addresses.filter(a => a.id !== addressId);
    addChangeDescription(`Removed address: ${removed?.value || addressId}`);
    onUpdate(updated);
  };

  return (
    <IconButton size="small" onClick={handleRemove} sx={{ ml: 0.5, color: '#d32f2f', p: 0.25 }}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

export const AddWebPresenceButton = ({ webPresence, onUpdate }) => {
  const { isEditMode, addChangeDescription, verificationId } = useEditMode();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [type, setType] = useState('WEB_REFERENCE');
  const [assessmentState, setAssessmentState] = useState(EMPTY_STRUCTURED_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isEditMode) return null;

  const resetForm = () => {
    setOpen(false);
    setValue('');
    setType('WEB_REFERENCE');
    setAssessmentState(EMPTY_STRUCTURED_STATE);
    setError(null);
    setSubmitting(false);
  };

  const fetchAutoAssessments = async (newNodeId) => {
    if (!STRUCTURED_TYPES.has(type)) return [];
    const inputs = buildStructuredInputs(type, assessmentState);
    if (!hasAnyStructuredInput(inputs)) return [];
    const response = await axios.post(
      `/ui/${verificationId}/web-presence/auto-assessments`,
      { type, node_id: newNodeId, inputs },
    );
    return response.data?.assessments || [];
  };

  const handleAdd = async () => {
    if (!value.trim()) return;
    setSubmitting(true);
    setError(null);
    const newId = crypto.randomUUID();
    let assessments = [];
    try {
      assessments = await fetchAutoAssessments(newId);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to build assessments');
      setSubmitting(false);
      return;
    }
    const entry = {
      id: newId,
      type,
      association_type: ['OWNS'],
      source: [{ source_url: value.trim(), fetched_at: new Date().toISOString() }],
      value: value.trim(),
      confidence: 100.0,
      assessments,
      data: null,
    };
    addChangeDescription(
      `Added web presence: ${type} - ${value.trim()}` +
        (assessments.length ? ` (${assessments.length} assessment${assessments.length === 1 ? '' : 's'})` : ''),
    );
    onUpdate([...webPresence, entry]);
    resetForm();
  };

  const renderAssessmentFields = () => {
    if (!STRUCTURED_TYPES.has(type)) return null;
    return (
      <>
        <Divider textAlign="left">
          <Typography variant="caption">{type} assessments</Typography>
        </Divider>
        <StructuredAssessmentFields
          type={type}
          state={assessmentState}
          onChange={setAssessmentState}
        />
      </>
    );
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ borderColor: '#4566EB', color: '#4566EB' }}
      >
        Add web presence
      </Button>

      <Dialog open={open} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle>Add web presence</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value)}
            >
              {WEB_PRESENCE_TYPES.map(t => (
                <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="URL"
            placeholder="https://..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
          />
          {renderAssessmentFields()}
          {error && <Typography color="error" variant="caption">{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!value.trim() || submitting}>
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RemoveWebPresenceButton = ({ itemId, webPresence, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const handleRemove = (e) => {
    e.stopPropagation();
    const removed = webPresence.find(w => w.id === itemId);
    const updated = webPresence.filter(w => w.id !== itemId);
    addChangeDescription(`Removed web presence: ${removed?.type} - ${removed?.value || itemId}`);
    onUpdate(updated);
  };

  return (
    <IconButton size="small" onClick={handleRemove} sx={{ ml: 0.5, color: '#d32f2f', p: 0.25 }}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

const PHONE_TYPES = [
  'INPUT_DATA',
  'VERIFIED_PHONE',
  'PUBLISHED_PHONE',
  'OTHER_PHONE',
];

export const AddPhoneButton = ({ phones, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [associationType, setAssociationType] = useState('INPUT_DATA');

  if (!isEditMode) return null;

  const handleAdd = () => {
    if (!value.trim()) return;
    const entry = {
      id: crypto.randomUUID(),
      type: 'PHONE',
      association_type: [associationType],
      source: [{ source_url: 'Manual edit', fetched_at: new Date().toISOString() }],
      value: value.trim(),
      confidence: 100.0,
      assessments: [],
      data: null,
    };
    addChangeDescription(`Added phone: ${value.trim()}`);
    onUpdate([...phones, entry]);

    setOpen(false);
    setValue('');
    setAssociationType('INPUT_DATA');
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ borderColor: '#4566EB', color: '#4566EB' }}
      >
        Add phone
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add phone number</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Phone Number"
            placeholder="(XXX) XXX-XXXX"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={associationType}
              label="Type"
              onChange={(e) => setAssociationType(e.target.value)}
            >
              {PHONE_TYPES.map(t => (
                <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!value.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RemovePhoneButton = ({ phoneId, phones, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const handleRemove = (e) => {
    e.stopPropagation();
    const removed = phones.find(p => p.id === phoneId);
    const updated = phones.filter(p => p.id !== phoneId);
    addChangeDescription(`Removed phone: ${removed?.value || phoneId}`);
    onUpdate(updated);
  };

  return (
    <IconButton size="small" onClick={handleRemove} sx={{ ml: 0.5, color: '#d32f2f', p: 0.25 }}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

export const AddEmailButton = ({ emails, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [associationType, setAssociationType] = useState('INPUT_DATA');

  if (!isEditMode) return null;

  const trimmed = value.trim();
  const isValid = EMAIL_REGEX.test(trimmed);

  const resetForm = () => {
    setOpen(false);
    setValue('');
    setAssociationType('INPUT_DATA');
  };

  const handleAdd = () => {
    if (!isValid) return;
    const entry = {
      id: crypto.randomUUID(),
      type: 'EMAIL',
      association_type: [associationType],
      source: [{ source_url: 'Manual edit', fetched_at: new Date().toISOString() }],
      value: trimmed,
      confidence: 100.0,
      assessments: [],
      data: null,
    };
    addChangeDescription(`Added email: ${trimmed}`);
    onUpdate([...emails, entry]);
    resetForm();
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ borderColor: '#4566EB', color: '#4566EB' }}
      >
        Add email
      </Button>

      <Dialog open={open} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle>Add email</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Email"
            type="email"
            placeholder="name@example.com"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={value !== '' && !isValid}
            helperText={value !== '' && !isValid ? 'Enter a valid email address' : ''}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={associationType}
              label="Type"
              onChange={(e) => setAssociationType(e.target.value)}
            >
              {EMAIL_TYPES.map(t => (
                <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!isValid}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RemoveEmailButton = ({ emailId, emails, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const handleRemove = (e) => {
    e.stopPropagation();
    const removed = emails.find(em => em.id === emailId);
    const updated = emails.filter(em => em.id !== emailId);
    addChangeDescription(`Removed email: ${removed?.value || emailId}`);
    onUpdate(updated);
  };

  return (
    <IconButton size="small" onClick={handleRemove} sx={{ ml: 0.5, color: '#d32f2f', p: 0.25 }}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

const WEBSITE_ASSOCIATION_TYPES = [
  { value: 'OWNS', label: 'Official website' },
  { value: 'INPUT_DATA', label: 'Submitted website' },
];

export const AddWebsiteButton = ({ webPresence, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [associationType, setAssociationType] = useState('OWNS');

  if (!isEditMode) return null;

  const resetForm = () => {
    setOpen(false);
    setValue('');
    setAssociationType('OWNS');
  };

  const handleAdd = () => {
    if (!value.trim()) return;
    const entry = {
      id: crypto.randomUUID(),
      type: 'DOMAIN',
      association_type: [associationType],
      source: [{ source_url: 'Manual edit', fetched_at: new Date().toISOString() }],
      value: value.trim(),
      confidence: 100.0,
      assessments: [],
      data: null,
    };
    addChangeDescription(`Added website: ${value.trim()}`);
    onUpdate([...webPresence, entry]);
    resetForm();
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ borderColor: '#4566EB', color: '#4566EB' }}
      >
        Add website
      </Button>

      <Dialog open={open} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle>Add website</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="URL"
            placeholder="https://..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={associationType}
              label="Type"
              onChange={(e) => setAssociationType(e.target.value)}
            >
              {WEBSITE_ASSOCIATION_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!value.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Pull current values out of whatever shape the existing whois data uses so the
// editor seeds with the values currently rendered on the card.
const readCreatedDate = (data) => {
  const raw = data?.registration_info?.created_date || data?.creation_date || data?.raw_whois_data?.creation_date;
  if (!raw) return '';
  const date = new Date(raw);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const readCity = (data) =>
  data?.registration_info?.registrant?.city ||
  data?.raw_whois_data?.registrant_city ||
  data?.registrant_city ||
  '';

const readState = (data) =>
  data?.registration_info?.registrant?.state_code ||
  data?.registration_info?.registrant?.state ||
  data?.raw_whois_data?.registrant_state ||
  data?.registrant_state ||
  '';

export const WebsiteWhoisEditor = ({ item, webPresence, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [open, setOpen] = useState(false);
  const [createdDate, setCreatedDate] = useState('');
  const [city, setCity] = useState('');
  const [registrantState, setRegistrantState] = useState('');

  if (!isEditMode || !onUpdate) return null;

  const openDialog = () => {
    setCreatedDate(readCreatedDate(item.data));
    setCity(readCity(item.data));
    setRegistrantState(readState(item.data));
    setOpen(true);
  };

  const handleSave = () => {
    const prevData = item.data || {};
    const prevReg = prevData.registration_info || {};
    const prevRegistrant = prevReg.registrant || {};

    // Store as UTC so the round-trip with readCreatedDate (toISOString) and the
    // card's getUTCDate() formatter doesn't drift the day for non-UTC users.
    const isoCreated = createdDate ? `${createdDate}T00:00:00Z` : null;
    const cityValue = city.trim() || null;
    const stateValue = registrantState.trim() || null;

    // The card and editor readers fall back across multiple whois paths. Write
    // the canonical path AND null the fallbacks so a cleared field doesn't
    // silently resurface from raw_whois_data / top-level keys on the next read.
    const newData = {
      ...prevData,
      creation_date: null,
      registrant_city: null,
      registrant_state: null,
      raw_whois_data: prevData.raw_whois_data
        ? {
            ...prevData.raw_whois_data,
            creation_date: null,
            registrant_city: null,
            registrant_state: null,
          }
        : prevData.raw_whois_data,
      registration_info: {
        ...prevReg,
        created_date: isoCreated,
        registrant: {
          ...prevRegistrant,
          city: cityValue,
          state_code: stateValue,
          state: null,
        },
      },
    };

    const updated = webPresence.map(wp =>
      wp.id === item.id ? { ...wp, data: newData } : wp
    );
    addChangeDescription(`Edited whois details for ${item.value}`);
    onUpdate(updated);
    setOpen(false);
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<EditIcon />}
        variant="outlined"
        onClick={openDialog}
        sx={{ borderColor: '#4566EB', color: '#4566EB' }}
      >
        Whois
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit whois details — {item.value}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Created date"
            type="date"
            value={createdDate}
            onChange={(e) => setCreatedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Registrant city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            fullWidth
          />
          <TextField
            label="Registrant state"
            placeholder="e.g. AZ"
            value={registrantState}
            onChange={(e) => setRegistrantState(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Mirrors isLienOrJudgment in LitigationsTab — kept inline to avoid a circular
// import (LitigationsTab already imports from this file).
const isLienFilingType = (originFilingType) => {
  const ft = (originFilingType || '').toUpperCase();
  return ft.includes('LIEN') || ft.includes('WARRANT') || ft.includes('JUDGMENT') || ft.includes('JUDGEMENT');
};

// Match the card's case_number first: LitigationsTab/LiensTab deduplicate raw
// rows by (case_number, node_id) and again globally by case_number, keeping
// only the first row's id. Matching by id would leave the other raw rows in
// place — they re-dedupe to the same card on next render and the card
// reappears. Fall back to origin_filing_number, then id for records that have
// no case_number and never went through dedup.
// recordType scopes case_number/origin_filing_number matches so we don't drop
// unrelated record types (civil_court vs lien_judgment vs bankruptcy) that
// happen to share a number. For lien_judgment, originFilingType further scopes
// by the lien-vs-litigation split (LiensTab shows those matching
// isLienOrJudgment; LitigationsTab shows the rest) so deleting from one tab
// can't silently remove a card on the other.
const pickLitigationMatcher = ({ caseNumber, originFilingNumber, recordId, recordType, originFilingType }) => {
  const isLienSide = recordType === 'lien_judgment' && originFilingType !== undefined
    ? isLienFilingType(originFilingType)
    : null;
  const sideOk = (r) => isLienSide === null || isLienFilingType(r.origin_filing_type) === isLienSide;
  // Mirror transformLitigationRecord's `record.record_type || 'civil_court'`
  // default so raw rows without record_type still match the civil_court card
  // they're rendered as.
  const typeOk = (r) => !recordType || (r.record_type || 'civil_court') === recordType;
  const scopeOk = (r) => typeOk(r) && sideOk(r);
  if (caseNumber) return (r) => scopeOk(r) && r.case_number === caseNumber;
  if (originFilingNumber) return (r) => scopeOk(r) && r.origin_filing_number === originFilingNumber;
  if (recordId) return (r) => r.id === recordId;
  return null;
};

export const RemoveUCCLienButton = ({ lienId, fileNumber, liens, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  // Match strictly on id — UCC file numbers can collide across states, so
  // legacy rows without an id are read-only to avoid dropping unrelated liens.
  const handleRemove = (e) => {
    e.stopPropagation();
    if (!lienId) return;
    const updated = {
      open: (liens?.open || []).filter(l => l.id !== lienId),
      closed: (liens?.closed || []).filter(l => l.id !== lienId),
    };
    addChangeDescription(`Removed UCC lien: #${fileNumber || lienId}`);
    onUpdate(updated);
  };

  return (
    <IconButton
      size="small"
      onClick={handleRemove}
      disabled={!lienId}
      title={lienId ? undefined : 'This row predates editing and cannot be deleted'}
      sx={{ ml: 0.5, color: '#d32f2f', p: 0.25 }}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

export const RemoveLitigationRecordButton = ({ caseNumber, originFilingNumber, recordId, recordType, originFilingType, litigations, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const matcher = pickLitigationMatcher({ caseNumber, originFilingNumber, recordId, recordType, originFilingType });

  const handleRemove = (e) => {
    e.stopPropagation();
    if (!matcher) return;
    const updated = {
      open: (litigations?.open || []).filter(r => !matcher(r)),
      closed: (litigations?.closed || []).filter(r => !matcher(r)),
    };
    const label = caseNumber || originFilingNumber || recordId;
    addChangeDescription(`Removed litigation record: #${label}`);
    onUpdate(updated);
  };

  return (
    <IconButton
      size="small"
      onClick={handleRemove}
      disabled={!matcher}
      sx={{ ml: 0.5, color: '#d32f2f', p: 0.25 }}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};
